import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, CheckCheck, CreditCard, Gift, Tag, ShieldAlert, Clock } from "lucide-react";
import { api, fmtDateTime, apiError } from "@/lib/api";
import { Skeleton, EmptyState } from "@/components/common";
import { useT } from "@/context/i18n";

const catMeta = {
  PAYMENT: { icon: CreditCard, tone: "text-emerald-600 bg-emerald-50" },
  EMI_DUE: { icon: Clock, tone: "text-amber-600 bg-amber-50" },
  REWARD: { icon: Gift, tone: "text-purple-600 bg-purple-50" },
  OFFER: { icon: Tag, tone: "text-blue-600 bg-blue-50" },
  SECURITY: { icon: ShieldAlert, tone: "text-red-600 bg-red-50" },
};

export default function Notifications() {
  const [items, setItems] = useState(null);
  const t = useT();
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const readOne = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
  const readAll = async () => { try { await api.post("/notifications/read-all"); toast.success("All marked as read"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16" />)}</div>;
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">{t("Notifications")}</h1><p className="text-sm text-slate-500">{t("EMI reminders, payments, rewards and account alerts.")}</p></div>
        {items.some((n) => !n.read) && <button onClick={readAll} data-testid="read-all" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><CheckCheck size={15} /> {t("Mark all read")}</button>}
      </div>
      {items.length === 0 ? <EmptyState icon={Bell} title={t("You're all caught up")} desc={t("New reminders and updates will appear here.")} /> : (
        <div className="space-y-2">
          {items.map((n) => {
            const m = catMeta[n.category] || { icon: Bell, tone: "text-slate-600 bg-slate-100" };
            return (
              <button key={n.id} onClick={() => !n.read && readOne(n.id)} data-testid={`notif-${n.id}`}
                className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition ${n.read ? "border-slate-200 bg-white" : "border-[#14B8A6]/30 bg-emerald-50/40"}`}>
                <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-md ${m.tone}`}><m.icon size={18} /></span>
                <span className="flex-1"><span className="flex items-center gap-2"><span className="font-semibold text-[#0B132B]">{n.title}</span>{!n.read && <span className="h-2 w-2 rounded-full bg-[#14B8A6]" />}</span><span className="mt-0.5 block text-sm text-slate-500">{n.body}</span><span className="mt-1 block text-xs text-slate-400">{fmtDateTime(n.created_at)}</span></span>
              </button>
            );
          })}
        </div>
      )}
      <p className="text-xs text-slate-400">Delivery channels (SMS, WhatsApp, Email, Push) are integration-ready.</p>
    </div>
  );
}
